import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mockData, type ABTest, type ChurnTrigger, type ModelRoutingRule, type PriceAdjustment } from '../api/client';
import { trpc } from '../api/trpc';

type ABTestStatus = 'draft' | 'running' | 'paused' | 'completed';

type Tab = 'ab-tests' | 'pricing' | 'churn' | 'routing';

// Modal types for creating new items
type NewABTest = {
  name: string;
  targetMetric: 'conversion' | 'retention' | 'revenue' | 'engagement';
  variants: { name: string; traffic: number; }[];
};

// Internal state type for editing variants (traffic can be temporarily empty)
type NewABTestState = {
  name: string;
  targetMetric: 'conversion' | 'retention' | 'revenue' | 'engagement';
  variants: { name?: string; traffic?: number; }[];
};

type NewChurnTrigger = {
  name: string;
  condition: string;
  action: 'send_email' | 'offer_discount' | 'notify_support' | 'pause_billing';
  enabled: boolean;
};

type NewRoutingRule = {
  name: string;
  condition: string;
  targetModel: string;
  priority: number;
  enabled: boolean;
};

export default function Optimization() {
  const [activeTab, setActiveTab] = useState<Tab>('ab-tests');
  const queryClient = useQueryClient();

  // Fetch real data from API with mock fallback
  const { data: abTestsData } = useQuery({
    queryKey: ['optimization', 'abTests'],
    queryFn: () => trpc.optimization.listABTests.query({}),
    placeholderData: { tests: mockData.abTests() },
  });

  const { data: churnData } = useQuery({
    queryKey: ['optimization', 'churnTriggers'],
    queryFn: () => trpc.optimization.listChurnTriggers.query(),
    placeholderData: { triggers: mockData.churnTriggers() },
  });

  const { data: routingData } = useQuery({
    queryKey: ['optimization', 'routingRules'],
    queryFn: () => trpc.optimization.listRoutingRules.query(),
    placeholderData: { rules: mockData.modelRoutingRules() },
  });

  const { data: pricingData } = useQuery({
    queryKey: ['optimization', 'priceAdjustments'],
    queryFn: () => trpc.optimization.listPriceAdjustments.query(),
    placeholderData: { adjustments: mockData.priceAdjustments() },
  });

  const abTests: ABTest[] = (abTestsData?.tests as ABTest[] | undefined) ?? mockData.abTests();
  const priceAdjustments: PriceAdjustment[] = (pricingData?.adjustments as PriceAdjustment[] | undefined) ?? mockData.priceAdjustments();
  const churnTriggers: ChurnTrigger[] = (churnData?.triggers as ChurnTrigger[] | undefined) ?? mockData.churnTriggers();
  const routingRules: ModelRoutingRule[] = (routingData?.rules as ModelRoutingRule[] | undefined) ?? mockData.modelRoutingRules();

  // A/B Test mutations
  const updateABTest = useMutation({
    mutationFn: (input: { id: string; status?: ABTestStatus; winnerId?: string }) =>
      trpc.optimization.updateABTest.mutate(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['optimization', 'abTests'] }),
  });

  // Churn trigger mutations
  const updateChurnTrigger = useMutation({
    mutationFn: (input: { id: string; enabled?: boolean }) =>
      trpc.optimization.updateChurnTrigger.mutate(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['optimization', 'churnTriggers'] }),
  });

  const deleteChurnTrigger = useMutation({
    mutationFn: (id: string) => trpc.optimization.deleteChurnTrigger.mutate({ id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['optimization', 'churnTriggers'] }),
  });

  // Routing rule mutations
  const updateRoutingRule = useMutation({
    mutationFn: (input: { id: string; enabled?: boolean }) =>
      trpc.optimization.updateRoutingRule.mutate(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['optimization', 'routingRules'] }),
  });

  const deleteRoutingRule = useMutation({
    mutationFn: (id: string) => trpc.optimization.deleteRoutingRule.mutate({ id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['optimization', 'routingRules'] }),
  });

  // Create mutations
  const createABTest = useMutation({
    mutationFn: (test: NewABTest) => trpc.optimization.createABTest.mutate(test),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['optimization', 'abTests'] }),
  });

  const createChurnTrigger = useMutation({
    mutationFn: (trigger: NewChurnTrigger) => trpc.optimization.createChurnTrigger.mutate(trigger),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['optimization', 'churnTriggers'] }),
  });

  const createRoutingRule = useMutation({
    mutationFn: (rule: NewRoutingRule) => trpc.optimization.createRoutingRule.mutate(rule),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['optimization', 'routingRules'] }),
  });

  const tabs = [
    { id: 'ab-tests' as Tab, name: 'A/B Tests', count: abTests.filter((t) => t.status === 'running').length },
    { id: 'pricing' as Tab, name: 'Pricing', count: priceAdjustments.filter((p) => p.status === 'pending').length },
    { id: 'churn' as Tab, name: 'Churn Triggers', count: churnTriggers.filter((t) => t.enabled).length },
    { id: 'routing' as Tab, name: 'Model Routing', count: routingRules.filter((r) => r.enabled).length },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Dynamic Optimization</h1>
        <p className="text-slate-400 mt-1">Manage experiments, pricing, and optimization rules</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-white hover:border-slate-600'
              }`}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-primary-500/20 text-primary-400' : 'bg-slate-700 text-slate-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'ab-tests' && <ABTestsTab tests={abTests} onUpdateTest={updateABTest.mutate} onCreateTest={createABTest.mutate} />}
      {activeTab === 'pricing' && <PricingTab adjustments={priceAdjustments} />}
      {activeTab === 'churn' && <ChurnTriggersTab triggers={churnTriggers} onUpdate={updateChurnTrigger.mutate} onDelete={deleteChurnTrigger.mutate} onCreate={createChurnTrigger.mutate} />}
      {activeTab === 'routing' && <ModelRoutingTab rules={routingRules} onUpdate={updateRoutingRule.mutate} onDelete={deleteRoutingRule.mutate} onCreate={createRoutingRule.mutate} />}
    </div>
  );
}

interface ABTestsTabProps {
  tests: ABTest[];
  onUpdateTest: (input: { id: string; status?: ABTestStatus; winnerId?: string }) => void;
  onCreateTest: (test: NewABTest) => void;
}

function ABTestsTab({ tests, onUpdateTest, onCreateTest }: ABTestsTabProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTest, setNewTest] = useState<NewABTestState>({
    name: '',
    targetMetric: 'conversion',
    variants: [
      { name: 'Control', traffic: 50 },
      { name: 'Variant A', traffic: 50 },
    ],
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-green-500/20 text-green-400';
      case 'completed':
        return 'bg-slate-600 text-slate-300';
      case 'draft':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-slate-600 text-slate-300';
    }
  };

  const handleCreateTest = () => {
    // Convert NewABTestState to NewABTest with required fields
    const validatedTest: NewABTest = {
      name: newTest.name,
      targetMetric: newTest.targetMetric,
      variants: newTest.variants.map(v => ({
        name: v.name ?? '',
        traffic: v.traffic ?? 0,
      })),
    };
    onCreateTest(validatedTest);
    setShowCreateModal(false);
    setNewTest({
      name: '',
      targetMetric: 'conversion',
      variants: [
        { name: 'Control', traffic: 50 },
        { name: 'Variant A', traffic: 50 },
      ],
    });
  };

  const addVariant = () => {
    const currentTotal = newTest.variants.reduce((sum, v) => sum + (v.traffic ?? 0), 0);
    const remaining = Math.max(0, 100 - currentTotal);
    setNewTest({
      ...newTest,
      variants: [...newTest.variants, { name: `Variant ${String.fromCharCode(65 + newTest.variants.length - 1)}`, traffic: remaining }],
    });
  };

  const removeVariant = (index: number) => {
    if (newTest.variants.length > 2) {
      setNewTest({
        ...newTest,
        variants: newTest.variants.filter((_, i) => i !== index),
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Active Experiments</h2>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">Create New Test</button>
      </div>

      {/* Create Test Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl shadow-xl w-full max-w-lg border border-slate-700">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">Create New A/B Test</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Test Name</label>
                <input
                  type="text"
                  value={newTest.name}
                  onChange={(e) => setNewTest({ ...newTest, name: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., Homepage CTA Button Color"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Target Metric</label>
                <select
                  value={newTest.targetMetric}
                  onChange={(e) => setNewTest({ ...newTest, targetMetric: e.target.value as NewABTest['targetMetric'] })}
                  className="input w-full"
                >
                  <option value="conversion">Conversion Rate</option>
                  <option value="retention">Retention</option>
                  <option value="revenue">Revenue</option>
                  <option value="engagement">Engagement</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-400">Variants</label>
                  <button onClick={addVariant} className="text-xs text-primary-400 hover:text-primary-300">+ Add Variant</button>
                </div>
                <div className="space-y-2">
                  {newTest.variants.map((variant, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={variant.name}
                        onChange={(e) => {
                          const variants = [...newTest.variants];
                          variants[index] = { ...variants[index], name: e.target.value };
                          setNewTest({ ...newTest, variants });
                        }}
                        className="input flex-1"
                        placeholder="Variant name"
                      />
                      <input
                        type="number"
                        value={variant.traffic}
                        onChange={(e) => {
                          const variants = [...newTest.variants];
                          variants[index] = { ...variants[index], traffic: parseInt(e.target.value) || 0 };
                          setNewTest({ ...newTest, variants });
                        }}
                        className="input w-20"
                        min={0}
                        max={100}
                      />
                      <span className="text-sm text-slate-400">%</span>
                      {newTest.variants.length > 2 && (
                        <button onClick={() => removeVariant(index)} className="text-red-400 hover:text-red-300">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Total traffic: {newTest.variants.reduce((sum, v) => sum + (v.traffic ?? 0), 0)}%
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-700">
              <button onClick={() => setShowCreateModal(false)} className="btn-secondary">Cancel</button>
              <button
                onClick={handleCreateTest}
                disabled={!newTest.name || newTest.variants.reduce((sum, v) => sum + (v.traffic ?? 0), 0) !== 100}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Test
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {tests.map((test) => (
          <div key={test.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium text-white">{test.name}</h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(test.status)}`}>
                    {test.status}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-1">
                  Target: {test.targetMetric.replace(/_/g, ' ')}
                  {test.startDate && ` | Started: ${new Date(test.startDate).toLocaleDateString()}`}
                </p>
              </div>
              <div className="flex gap-2">
                {test.status === 'running' && (
                  <>
                    <button
                      onClick={() => onUpdateTest({ id: test.id, status: 'paused' })}
                      className="btn-secondary text-sm px-3 py-1.5"
                    >
                      Pause
                    </button>
                    <button
                      onClick={() => onUpdateTest({ id: test.id, status: 'completed' })}
                      className="bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg hover:bg-green-500/30 transition-colors font-medium text-sm"
                    >
                      Declare Winner
                    </button>
                  </>
                )}
                {test.status === 'draft' && (
                  <button
                    onClick={() => onUpdateTest({ id: test.id, status: 'running' })}
                    className="btn-primary text-sm px-3 py-1.5"
                  >
                    Start Test
                  </button>
                )}
                {test.status === 'paused' && (
                  <button
                    onClick={() => onUpdateTest({ id: test.id, status: 'running' })}
                    className="btn-primary text-sm px-3 py-1.5"
                  >
                    Resume
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {test.variants.map((variant) => {
                const isWinning = test.status === 'running' &&
                  variant.conversionRate === Math.max(...test.variants.map((v) => v.conversionRate));
                return (
                  <div
                    key={variant.id}
                    className={`p-4 rounded-lg border ${
                      isWinning ? 'border-green-500/30 bg-green-500/10' : 'border-slate-700 bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-white">{variant.name}</span>
                      {isWinning && (
                        <span className="text-xs text-green-400 font-medium">Leading</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Traffic</span>
                        <span className="text-white">{variant.traffic}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Conversions</span>
                        <span className="text-white">{variant.conversions.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Rate</span>
                        <span className={`font-medium ${isWinning ? 'text-green-400' : 'text-white'}`}>
                          {variant.conversionRate}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingTab({ adjustments }: { adjustments: PriceAdjustment[] }) {
  const [localAdjustments, setLocalAdjustments] = useState(adjustments);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisMessage, setAnalysisMessage] = useState<string | null>(null);

  const handleApprove = (id: string) => {
    setLocalAdjustments(prev => prev.map(adj =>
      adj.id === id ? { ...adj, status: 'approved' as const } : adj
    ));
  };

  const handleReject = (id: string) => {
    setLocalAdjustments(prev => prev.map(adj =>
      adj.id === id ? { ...adj, status: 'rejected' as const } : adj
    ));
  };

  const runPriceAnalysis = () => {
    setIsAnalyzing(true);
    setAnalysisMessage(null);
    // Simulate API call
    setTimeout(() => {
      setIsAnalyzing(false);
      setAnalysisMessage('Price analysis complete. 2 new suggestions generated based on current market data and usage patterns.');
    }, 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Suggested Price Adjustments</h2>
        <button
          onClick={runPriceAnalysis}
          disabled={isAnalyzing}
          className="btn-primary disabled:opacity-50"
        >
          {isAnalyzing ? 'Analyzing...' : 'Run Price Analysis'}
        </button>
      </div>

      {analysisMessage && (
        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
          <p className="text-sm text-green-400">{analysisMessage}</p>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50 border-b border-slate-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Tier</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Current Price</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Suggested Price</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Change</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Reason</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {localAdjustments.map((adj) => {
              const change = ((adj.suggestedPrice - adj.currentPrice) / adj.currentPrice) * 100;
              return (
                <tr key={adj.id} className="hover:bg-slate-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium text-white capitalize">{adj.tier}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-slate-300">
                    ${adj.currentPrice}/mo
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-white">
                    ${adj.suggestedPrice}/mo
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-green-400 font-medium">+{change.toFixed(1)}%</span>
                  </td>
                  <td className="px-6 py-4 max-w-xs">
                    <p className="text-sm text-slate-400 truncate" title={adj.reason}>
                      {adj.reason}
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {adj.status === 'pending' ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(adj.id)}
                          className="bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg hover:bg-green-500/30 transition-colors font-medium text-sm"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(adj.id)}
                          className="bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg hover:bg-red-500/30 transition-colors font-medium text-sm"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                        adj.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {adj.status}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Price Sensitivity Analysis</h3>
        <div className="grid grid-cols-3 gap-6">
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Price Elasticity</p>
            <p className="text-2xl font-bold text-white mt-1">-0.42</p>
            <p className="text-sm text-slate-400 mt-1">1% price increase = 0.42% demand decrease</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Optimal Price Point</p>
            <p className="text-2xl font-bold text-white mt-1">$67/mo</p>
            <p className="text-sm text-slate-400 mt-1">Maximizes revenue for Pro tier</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Revenue Impact</p>
            <p className="text-2xl font-bold text-green-400 mt-1">+$18,500/mo</p>
            <p className="text-sm text-slate-400 mt-1">If all suggestions approved</p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ChurnTriggersTabProps {
  triggers: ChurnTrigger[];
  onUpdate: (input: { id: string; enabled?: boolean; name?: string; condition?: string; action?: 'send_email' | 'offer_discount' | 'notify_support' | 'pause_billing' }) => void;
  onDelete: (id: string) => void;
  onCreate: (trigger: NewChurnTrigger) => void;
}

function ChurnTriggersTab({ triggers, onUpdate, onDelete, onCreate }: ChurnTriggersTabProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState<ChurnTrigger | null>(null);
  const [newTrigger, setNewTrigger] = useState<NewChurnTrigger>({
    name: '',
    condition: '',
    action: 'send_email',
    enabled: true,
  });

  const toggleTrigger = (id: string, currentEnabled: boolean) => {
    onUpdate({ id, enabled: !currentEnabled });
  };

  const handleCreate = () => {
    onCreate(newTrigger);
    setShowCreateModal(false);
    setNewTrigger({ name: '', condition: '', action: 'send_email', enabled: true });
  };

  const handleSaveEdit = () => {
    if (editingTrigger) {
      onUpdate({
        id: editingTrigger.id,
        name: editingTrigger.name,
        condition: editingTrigger.condition,
        action: editingTrigger.action as NewChurnTrigger['action'],
      });
      setEditingTrigger(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Churn Prevention Triggers</h2>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">Add New Trigger</button>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTrigger) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl shadow-xl w-full max-w-lg border border-slate-700">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">
                {editingTrigger ? 'Edit Trigger' : 'Create New Trigger'}
              </h2>
              <button onClick={() => { setShowCreateModal(false); setEditingTrigger(null); }} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Name</label>
                <input
                  type="text"
                  value={editingTrigger?.name ?? newTrigger.name}
                  onChange={(e) => editingTrigger
                    ? setEditingTrigger({ ...editingTrigger, name: e.target.value })
                    : setNewTrigger({ ...newTrigger, name: e.target.value })
                  }
                  className="input w-full"
                  placeholder="e.g., Low Usage Warning"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Condition</label>
                <input
                  type="text"
                  value={editingTrigger?.condition ?? newTrigger.condition}
                  onChange={(e) => editingTrigger
                    ? setEditingTrigger({ ...editingTrigger, condition: e.target.value })
                    : setNewTrigger({ ...newTrigger, condition: e.target.value })
                  }
                  className="input w-full"
                  placeholder='e.g., usage_days < 7 AND tier = "pro"'
                />
                <p className="text-xs text-slate-500 mt-1">Use field names: no_login_days, credits_remaining, weekly_usage_change, payment_failures, tier</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Action</label>
                <select
                  value={editingTrigger?.action ?? newTrigger.action}
                  onChange={(e) => {
                    const action = e.target.value as NewChurnTrigger['action'];
                    editingTrigger
                      ? setEditingTrigger({ ...editingTrigger, action })
                      : setNewTrigger({ ...newTrigger, action });
                  }}
                  className="input w-full"
                >
                  <option value="send_email">Send Re-engagement Email</option>
                  <option value="offer_discount">Offer Discount</option>
                  <option value="notify_support">Notify Support Team</option>
                  <option value="pause_billing">Pause Billing</option>
                </select>
              </div>
              {!editingTrigger && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={newTrigger.enabled}
                    onChange={(e) => setNewTrigger({ ...newTrigger, enabled: e.target.checked })}
                    className="rounded border-slate-600 bg-slate-700 text-primary-500"
                  />
                  <label htmlFor="enabled" className="text-sm text-slate-300">Enable immediately</label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-700">
              <button onClick={() => { setShowCreateModal(false); setEditingTrigger(null); }} className="btn-secondary">Cancel</button>
              <button
                onClick={editingTrigger ? handleSaveEdit : handleCreate}
                disabled={!(editingTrigger?.name ?? newTrigger.name) || !(editingTrigger?.condition ?? newTrigger.condition)}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingTrigger ? 'Save Changes' : 'Create Trigger'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50 border-b border-slate-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Name</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Condition</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Action</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {triggers.map((trigger) => (
              <tr key={trigger.id} className="hover:bg-slate-700/50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="font-medium text-white">{trigger.name}</span>
                </td>
                <td className="px-6 py-4">
                  <code className="text-sm bg-slate-700 px-2 py-1 rounded text-slate-300">
                    {trigger.condition}
                  </code>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-slate-300">{trigger.action.replace(/_/g, ' ')}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => toggleTrigger(trigger.id, trigger.enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      trigger.enabled ? 'bg-primary-600' : 'bg-slate-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        trigger.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingTrigger(trigger)}
                      className="text-primary-400 hover:text-primary-300 font-medium text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(trigger.id)}
                      className="text-red-400 hover:text-red-300 font-medium text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="card">
          <p className="text-sm text-slate-400">Triggered This Week</p>
          <p className="text-3xl font-bold text-white mt-2">247</p>
          <p className="text-sm text-green-400 mt-1">+12% from last week</p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-400">Churn Prevented</p>
          <p className="text-3xl font-bold text-green-400 mt-2">34</p>
          <p className="text-sm text-slate-400 mt-1">Users retained after intervention</p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-400">Revenue Saved</p>
          <p className="text-3xl font-bold text-white mt-2">$4,230</p>
          <p className="text-sm text-slate-400 mt-1">MRR preserved</p>
        </div>
      </div>
    </div>
  );
}

interface ModelRoutingTabProps {
  rules: ModelRoutingRule[];
  onUpdate: (input: { id: string; enabled?: boolean; name?: string; condition?: string; targetModel?: string; priority?: number }) => void;
  onDelete: (id: string) => void;
  onCreate: (rule: NewRoutingRule) => void;
}

function ModelRoutingTab({ rules, onUpdate, onDelete, onCreate }: ModelRoutingTabProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ModelRoutingRule | null>(null);
  const [newRule, setNewRule] = useState<NewRoutingRule>({
    name: '',
    condition: '',
    targetModel: 'claude-3-sonnet',
    priority: 5,
    enabled: true,
  });

  const toggleRule = (id: string, currentEnabled: boolean) => {
    onUpdate({ id, enabled: !currentEnabled });
  };

  const handleCreate = () => {
    onCreate(newRule);
    setShowCreateModal(false);
    setNewRule({ name: '', condition: '', targetModel: 'claude-3-sonnet', priority: 5, enabled: true });
  };

  const handleSaveEdit = () => {
    if (editingRule) {
      onUpdate({
        id: editingRule.id,
        name: editingRule.name,
        condition: editingRule.condition,
        targetModel: editingRule.targetModel,
        priority: editingRule.priority,
      });
      setEditingRule(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Model Routing Rules</h2>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">Add New Rule</button>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingRule) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl shadow-xl w-full max-w-lg border border-slate-700">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">
                {editingRule ? 'Edit Routing Rule' : 'Create New Routing Rule'}
              </h2>
              <button onClick={() => { setShowCreateModal(false); setEditingRule(null); }} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Rule Name</label>
                <input
                  type="text"
                  value={editingRule?.name ?? newRule.name}
                  onChange={(e) => editingRule
                    ? setEditingRule({ ...editingRule, name: e.target.value })
                    : setNewRule({ ...newRule, name: e.target.value })
                  }
                  className="input w-full"
                  placeholder="e.g., Enterprise Priority Routing"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Condition</label>
                <input
                  type="text"
                  value={editingRule?.condition ?? newRule.condition}
                  onChange={(e) => editingRule
                    ? setEditingRule({ ...editingRule, condition: e.target.value })
                    : setNewRule({ ...newRule, condition: e.target.value })
                  }
                  className="input w-full"
                  placeholder='e.g., user.tier = "enterprise" AND task_complexity > 0.7'
                />
                <p className="text-xs text-slate-500 mt-1">Use: user.tier, task_complexity, task_type, token_estimate, default</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Target Model</label>
                <select
                  value={editingRule?.targetModel ?? newRule.targetModel}
                  onChange={(e) => editingRule
                    ? setEditingRule({ ...editingRule, targetModel: e.target.value })
                    : setNewRule({ ...newRule, targetModel: e.target.value })
                  }
                  className="input w-full"
                >
                  <option value="claude-3-opus">Claude 3 Opus</option>
                  <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                  <option value="claude-3-haiku">Claude 3 Haiku</option>
                  <option value="claude-2.1">Claude 2.1</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Priority (1 = highest)</label>
                <input
                  type="number"
                  value={editingRule?.priority ?? newRule.priority}
                  onChange={(e) => {
                    const priority = parseInt(e.target.value) || 1;
                    editingRule
                      ? setEditingRule({ ...editingRule, priority })
                      : setNewRule({ ...newRule, priority });
                  }}
                  className="input w-full"
                  min={1}
                  max={100}
                />
              </div>
              {!editingRule && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="ruleEnabled"
                    checked={newRule.enabled}
                    onChange={(e) => setNewRule({ ...newRule, enabled: e.target.checked })}
                    className="rounded border-slate-600 bg-slate-700 text-primary-500"
                  />
                  <label htmlFor="ruleEnabled" className="text-sm text-slate-300">Enable immediately</label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-700">
              <button onClick={() => { setShowCreateModal(false); setEditingRule(null); }} className="btn-secondary">Cancel</button>
              <button
                onClick={editingRule ? handleSaveEdit : handleCreate}
                disabled={!(editingRule?.name ?? newRule.name) || !(editingRule?.condition ?? newRule.condition)}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingRule ? 'Save Changes' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50 border-b border-slate-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Priority</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Name</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Condition</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Target Model</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {[...rules].sort((a, b) => a.priority - b.priority).map((rule) => (
              <tr key={rule.id} className="hover:bg-slate-700/50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-600 text-slate-300">
                    {rule.priority}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="font-medium text-white">{rule.name}</span>
                </td>
                <td className="px-6 py-4">
                  <code className="text-sm bg-slate-700 px-2 py-1 rounded text-slate-300">
                    {rule.condition}
                  </code>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                    {rule.targetModel}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => toggleRule(rule.id, rule.enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      rule.enabled ? 'bg-primary-600' : 'bg-slate-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        rule.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingRule(rule)}
                      className="text-primary-400 hover:text-primary-300 font-medium text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(rule.id)}
                      className="text-red-400 hover:text-red-300 font-medium text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Routing Performance</h3>
        <div className="grid grid-cols-4 gap-6">
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Total Requests Routed</p>
            <p className="text-2xl font-bold text-white mt-1">1.2M</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Avg Latency</p>
            <p className="text-2xl font-bold text-white mt-1">142ms</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Cost Savings</p>
            <p className="text-2xl font-bold text-green-400 mt-1">$8,450</p>
          </div>
          <div className="p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400">Quality Score</p>
            <p className="text-2xl font-bold text-white mt-1">94.2%</p>
          </div>
        </div>
      </div>
    </div>
  );
}
